import { useRef, useEffect, useMemo, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useConfiguratorContext, useConfiguratorEvent } from '@augmatica/iframe-interface-react'

import { isControllableItem } from './helpers/isControllableItem'
import { getDmArgsFromUrl } from './helpers/getDmArgsFromUrl'

import {
  fetchContent,
  fetchPartPaymentWidget,
  loadConfig,
  fetchDataForItems_aggregated,
  fetchPrices_aggregated,
  addToCart_aggregated,
  submitForm_aggregated,
} from './store/requests/thunks'

import { handleCustomer } from './store/customer/customerSlice'
import {
  addSubstitutedArticles,
  clearMissingErpItemNumbersToReport,
  markReportedArticlesUnresolved,
  setIsAwaitingArticleSubstitutions,
} from './store/articleSubstitutions/articleSubstitutionsSlice'
import { handleFilterChangeForSelectedItem } from './store/items/itemsSlice'
import {
  setIsArticleSubstitutionsModalOpen,
  setIsEditItemPanelActive,
  setIsFailedToLoadProjectModalOpen,
  setLoftSelected,
  setIsDelayedDeliverySelected,
} from './store/ui/uiSlice'
import { handleAppearanceUpdate } from './store/model/modelSlice'
import { handleSelectedItem, handleSlotOptions } from './store/selectedItem/selectedItemSlice'
import { flattenBom } from './helpers/flattenBom'

import { CART, OFFER, SAVE } from './constants/constants'
import ConfiguratorView from './views/ConfiguratorView'
import ErrorView from './views/ErrorView'
import AppHeader from './components/AppHeader/AppHeader'
import Faq from './components/Faq/Faq'
import Message from './components/Message/Message'
import SaveProject from './components/SaveProject/SaveProject'
import UserGuide from './components/UserGuide/UserGuide'
import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from './hooks'
import type * as DynamakerContract from './integration/DynamakerContract'

const faqElement = document.querySelector('#mok-context-spa-banners > .faq')
let faqHTML = null
if (faqElement) {
  faqHTML = faqElement.outerHTML
  faqElement.remove()
}

const userGuideElement = document.querySelector('#mok-context-spa-banners > #user-guide')
let userGuideHTML = null
if (userGuideElement) {
  userGuideHTML = userGuideElement.outerHTML
  userGuideElement.remove()
}

const App: React.FC = () => {
  const { channelId } = useAppSelector((state) => state.init)
  const { isEditItemPanelActive, isSummaryPanelVisible } = useAppSelector((state) => state.ui)
  const { isAwaitingArticleSubstitutions, missingErpItemNumbersToReport } = useAppSelector(
    (state) => state.articleSubstitutions
  )
  const { selectedItem } = useAppSelector((state) => state.selectedItem)
  const { configId: configIdFromYass, configData: configDataFromYass } = useAppSelector((state) => state.yass)

  const configurator = useConfiguratorContext()
  const configuratorRef = useRef(configurator)
  const latestConfigResultRef = useRef<Omit<DynamakerContract.ConfigResult, 'slotOptions'> | null>(null)

  const dispatch = useAppDispatch()
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const configIdFromUrl = urlParams.get('configId') ?? urlParams.get('configid')
  const configHashKeyFromUrl = urlParams.get('configHashKey') ?? urlParams.get('confighashkey')?.toUpperCase()
  const initWithDefaultProject = !configIdFromUrl

  const handleConfigResult = useCallback(
    (configResult: Omit<DynamakerContract.ConfigResult, 'slotOptions'>) => {
      latestConfigResultRef.current = configResult
      const itemsList = Object.values(configResult.items)
      // @ts-expect-error //TODO 5625: remove
      configResult.bom = configResult.configurationSections // TODO 5625
      dispatch(
        fetchDataForItems_aggregated({
          items: ([] as (typeof itemsList)[0]).concat(...itemsList),
          articlesInConfig: flattenBom(configResult.bom).reduce(
            (skus, article) => (article.sku ? skus.add(article.sku) : skus),
            new Set<string>()
          ),
        })
      )
        .then(() => {
          dispatch(fetchPrices_aggregated(configResult))
            .unwrap()
            .then(
              ({
                bomItemsWithUpdatedConfigData,
              }: {
                bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
              }) => {
                if (bomItemsWithUpdatedConfigData.length) {
                  configuratorRef.current.updateItems(
                    bomItemsWithUpdatedConfigData.map((bomItem) => ({
                      item: bomItem.item,
                      id: bomItem.id,
                      props: { configData: bomItem.configData } as any,
                    })),
                    { silent: true }
                  )
                }
              }
            )
            .catch((error) => {
              // Skipping the call while DM works on substitutes is deliberate, not a failure
              if (error?.name === 'ConditionError' || error?._abortedAwaitingSubstitutions) {
                if (import.meta.env.VITE_SCENSUM_ENV !== 'production') {
                  console.log(
                    'mok-context-spa in dev/test env: fetchPrices_aggregated skipped, awaiting article substitutions from DynaMaker.'
                  )
                }
                return
              }
              console.error('Error in handleConfigResult:', error)
            })
        })
        .catch((error) => {
          console.error('Error in handleConfigResult:', error)
        })
    },
    [dispatch]
  )

  // DM sends no configuration-update for this choice, so its latest configuration is run through again
  // as the base for pricing, with the delayed delivery article added or removed on top
  const handleDelayedDeliveryChange = useCallback(
    (isSelected: boolean) => {
      dispatch(setIsDelayedDeliverySelected(isSelected))

      if (!latestConfigResultRef.current) {
        console.error('Delayed delivery was changed before any configuration was received from DM')
        return
      }
      handleConfigResult(latestConfigResultRef.current)
    },
    [dispatch, handleConfigResult]
  )

  // On DM application error
  useConfiguratorEvent(
    'error',
    (data) => {
      console.error('Error event from DM:', JSON.stringify(data))

      // DM answers with an error when it has no substitute for an article we reported as missing
      // DM is usable afterwards
      if (isAwaitingArticleSubstitutions) {
        // Released first, or the condition on fetchPrices_aggregated blocks the re-run below
        dispatch(setIsAwaitingArticleSubstitutions(false))
        dispatch(markReportedArticlesUnresolved())
        dispatch(setIsArticleSubstitutionsModalOpen(true))
        if (latestConfigResultRef.current) {
          handleConfigResult(latestConfigResultRef.current)
        }
      }
    },
    [isAwaitingArticleSubstitutions]
  )

  // On model appearance update in DM application
  useConfiguratorEvent('appearance-update', (appearance) => dispatch(handleAppearanceUpdate(appearance)), [dispatch])

  // On item selected in DM application
  useConfiguratorEvent(
    'item-selected',
    ({ slotOptions, ...newSelectedItem }: DynamakerContract.ItemSelected['data']) => {
      dispatch(handleSlotOptions({ slotOptions }))
      dispatch(handleSelectedItem(newSelectedItem))

      if (isControllableItem(newSelectedItem)) {
        dispatch(handleFilterChangeForSelectedItem(newSelectedItem))
      }

      if (isEditItemPanelActive && newSelectedItem.id !== selectedItem.id) {
        dispatch(setIsEditItemPanelActive(false))
        configuratorRef.current.cameraToPrevious()
      }
    },
    [selectedItem, isEditItemPanelActive]
  )

  // On default project ready from DM
  useConfiguratorEvent(
    'default-project-ready',
    ({ slotOptions, ...configResult }: DynamakerContract.DefaultProjectReady['data']) => {
      // When the default project is ready from DM (not initializing with a project to load)
      dispatch(handleSlotOptions({ slotOptions, _dmEvent: 'default-project-ready' }))
      handleConfigResult(configResult)
    }
  )

  // On configuration update from DM
  useConfiguratorEvent(
    'configuration-update',
    ({ slotOptions, substitutedArticles, ...configResult }: DynamakerContract.ConfigurationUpdate['data']) => {
      if (substitutedArticles?.length) {
        dispatch(addSubstitutedArticles(substitutedArticles))
        dispatch(setIsArticleSubstitutionsModalOpen(true))
        dispatch(setIsAwaitingArticleSubstitutions(false))
      }
      dispatch(handleSlotOptions({ slotOptions, _dmEvent: 'configuration-update' }))
      handleConfigResult(configResult)
    }
  )

  // Effects run in order, and the ones below read configuratorRef
  useEffect(() => {
    configuratorRef.current = configurator
  }, [configurator])

  useEffect(() => {
    if (missingErpItemNumbersToReport.length === 0) return

    configuratorRef.current.reportMissingArticles(missingErpItemNumbersToReport)

    dispatch(clearMissingErpItemNumbersToReport())
  }, [missingErpItemNumbersToReport])

  useEffect(() => {
    const promises: any[] = []

    dispatch(fetchContent())
    dispatch(fetchPartPaymentWidget())

    const sibhcFromUrl = urlParams.get('sibhc')
    if (sibhcFromUrl) {
      dispatch(handleCustomer({ cnohash: sibhcFromUrl }))
    }

    if (configIdFromUrl && configHashKeyFromUrl) {
      promises.push(
        dispatch(
          loadConfig({
            configId: configIdFromUrl,
            configHashKey: configHashKeyFromUrl,
          })
        )
      )
    }

    // Cleanup
    return () => {
      promises.forEach((promise) => promise.abort())
    }
  }, [])

  useEffect(() => {
    if (configDataFromYass) {
      configuratorRef.current
        .loadProject(configDataFromYass)
        .then((dmData) => {
          const { slotOptions, substitutedArticles, ...configResult } =
            dmData as DynamakerContract.LoadedProjectReady['data']
          if (substitutedArticles?.length) {
            dispatch(addSubstitutedArticles(substitutedArticles))
            dispatch(setIsArticleSubstitutionsModalOpen(true))
          }
          dispatch(setLoftSelected(!!configResult.props.loftLeft.value || !!configResult.props.loftRight.value))
          dispatch(handleSlotOptions({ slotOptions }))
          handleConfigResult(configResult)
        })
        .catch((error) => {
          console.error(`DM loadProject was rejected (failed), configId (from YASS): ${configIdFromYass}`)
          console.error(error)
          setIsFailedToLoadProjectModalOpen(true)
        })
    }
  }, [configDataFromYass])

  useEffect(() => {
    if (isSummaryPanelVisible) {
      configuratorRef.current.setMode('view')
    } else {
      configuratorRef.current.setMode('configure')
    }
  }, [isSummaryPanelVisible])

  const forceDmTestEnv = urlParams.get('dmEnv') === 'test'
  const forceDmProdEnv = urlParams.get('dmEnv') === 'prod'
  const dmArgsFromUrl = useMemo(() => getDmArgsFromUrl(urlParams), [urlParams])

  if ((configIdFromUrl && !configHashKeyFromUrl) || (!configIdFromUrl && configHashKeyFromUrl)) {
    console.warn('configId or configHashKey is missing in urlParams')
    return <ErrorView channelId={channelId} />
  }

  const onRequestSaveProject = useCallback(
    ({ type, customer }: any): { internalSaveUuidv4: string } => {
      if (type !== CART && type !== OFFER && type !== SAVE) {
        console.warn("Unexpected or missing value for 'type' in App component")
      }

      const internalSaveUuidv4 = uuidv4()

      if (customer) {
        dispatch(handleCustomer(customer))
      }

      configuratorRef.current
        .saveProject()
        .then((projectData: DynamakerContract.SavefileReady['data']) => {
          // @ts-expect-error // TODO 5625 remove
          const { config, configurationSections: bom, meta, props, items } = projectData
          dispatch(
            type === CART
              ? addToCart_aggregated({
                  config,
                  bom,
                  meta,
                  props,
                  items,
                  internalSaveUuidv4,
                })
              : submitForm_aggregated({
                  config,
                  bom,
                  meta,
                  props,
                  items,
                  internalSaveUuidv4,
                  type,
                })
          )
            .unwrap()
            .then(
              ({
                bomItemsWithUpdatedConfigData,
              }: {
                bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
              }) => {
                configuratorRef.current.updateItems(
                  bomItemsWithUpdatedConfigData.map((bomItem) => ({
                    item: bomItem.item,
                    id: bomItem.id,
                    props: { configData: bomItem.configData } as any,
                  })),
                  { silent: false } // will cause a 'configuration-update' event from DM which will dispatch the action 'fetchPrices_aggregated'
                )
              }
            )
            .catch((error) => {
              console.error(error)
            })
        })
        .catch((error) => {
          console.error(error)
        })

      return { internalSaveUuidv4 }
    },
    [dispatch]
  )

  return (
    <Styled.App>
      <AppHeader
        onRequestSaveProject={onRequestSaveProject} // Add to cart
      />

      <ConfiguratorView
        forceDmTestEnv={forceDmTestEnv}
        forceDmProdEnv={forceDmProdEnv}
        dmArgsFromUrl={dmArgsFromUrl}
        awaitProjectLoaded={!initWithDefaultProject}
        onRequestSaveProject={onRequestSaveProject}
        onDelayedDeliveryChange={handleDelayedDeliveryChange}
      />

      <SaveProject onRequestSaveProject={onRequestSaveProject} />
      {faqHTML && <Faq faqHTML={faqHTML} />}
      {userGuideHTML && <UserGuide userGuideHTML={userGuideHTML} />}

      <Message />
    </Styled.App>
  )
}

export default App
